import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { ServiceCard, ContactCard } from '@/components/Cards';
import { SERVICES } from '@/data';
import { CONTACT_INFO } from '@/lib';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';
import { useState } from 'react';

export default function Home() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <Layout>
      <section id="inicio" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.HERO_BG_1} 
            alt="Escritório de contabilidade profissional" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-background via-background/95 to-primary/10" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--primary)_0%,_transparent_50%)] opacity-10" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
          >
            <motion.h1 
              className="text-6xl md:text-8xl font-extrabold mb-6 bg-gradient-to-r from-foreground via-foreground to-primary bg-clip-text text-transparent"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.1 }}
            >
              Leandro Silva
            </motion.h1>
            <motion.p 
              className="text-2xl md:text-4xl font-bold text-primary mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.15 }}
            >
              Técnico Especialista em Contabilidade e Fiscalidade
            </motion.p>
            <motion.p 
              className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.2 }}
            >
              Atendimento remoto em todo o país • Sede em Castro Daire
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.3 }}
            >
              <Button 
                size="lg" 
                className="text-lg px-10 py-7 shadow-2xl hover:shadow-primary/50 hover:scale-105 transition-all duration-300 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
                onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Solicitar Contacto
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section id="servicos" className="py-24 relative">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.SERVICOS_BG_1} 
            alt="Serviços de contabilidade" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={springPresets.gentle}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">Serviços</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Oferecemos uma gama completa de serviços contabilísticos e fiscais adaptados às suas necessidades
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {SERVICES.map((service) => (
              <motion.div key={service.id} variants={staggerItem}>
                <ServiceCard service={service} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="sobre" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-muted/50 via-background to-primary/5" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={springPresets.gentle}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-accent/20 rounded-3xl blur-2xl" />
              <img 
                src={IMAGES.IMAGE_6483441_36} 
                alt="Leandro Silva - Técnico de Contabilidade" 
                className="relative w-full h-auto rounded-3xl shadow-2xl border-4 border-background"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={springPresets.gentle}
            >
              <h2 className="text-4xl md:text-6xl font-extrabold mb-8 bg-gradient-to-r from-foreground to-primary bg-clip-text text-transparent">Sobre Mim</h2>
              <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                <p>
                  Sou o <strong className="text-foreground">Leandro Silva</strong>, Técnico Especialista em Contabilidade e Fiscalidade. 
                  Tenho o privilégio de trabalhar com vários clientes de diversas zonas do país, oferecendo um serviço 
                  totalmente remoto que me permite estar disponível para apoiar pessoas e empresas em qualquer localização.
                </p>
                <p>
                  O <strong className="text-foreground">trabalho remoto</strong> é uma das minhas grandes vantagens competitivas. 
                  Permite-me dar resposta rápida e eficaz às necessidades dos meus clientes, independentemente da sua localização geográfica. 
                  A minha sede está em <strong className="text-foreground">Castro Daire</strong>, mas atuo em todo o território nacional.
                </p>
                <p>
                  Os meus serviços incluem <strong className="text-foreground">declarações de IRS</strong>, gestão de <strong className="text-foreground">IVA</strong>, 
                  <strong className="text-foreground"> lançamentos contabilísticos</strong>, apoio em questões de <strong className="text-foreground">Segurança Social</strong>, 
                  e muito mais. Ofereço também <strong className="text-foreground">apoio administrativo</strong> completo para libertar o seu tempo e permitir que se foque no que realmente importa.
                </p>
                <p className="font-semibold text-foreground">
                  A minha missão é simplificar a sua vida fiscal e contabilística, garantindo conformidade legal e tranquilidade financeira. 
                  Conte comigo como seu parceiro de confiança!
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="contacto" className="py-24 relative">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={springPresets.gentle}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">Contacto</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Entre em contacto para esclarecer dúvidas ou solicitar os meus serviços. Estou disponível para atendê-lo!
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={springPresets.gentle}
            >
              <div className="bg-card rounded-2xl p-8 shadow-lg border border-border">
                <h3 className="text-2xl font-semibold mb-6 text-foreground">Envie uma Mensagem</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Input 
                      type="text" 
                      name="name"
                      placeholder="Nome Completo" 
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Input 
                      type="email" 
                      name="email"
                      placeholder="Email" 
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Input 
                      type="tel" 
                      name="phone"
                      placeholder="Telefone" 
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Textarea 
                      name="message"
                      placeholder="Mensagem" 
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full resize-none"
                    />
                  </div>
                  <Button type="submit" size="lg" className="w-full">
                    <Send className="mr-2 h-5 w-5" />
                    Enviar Mensagem
                  </Button>
                </form>
              </div>
            </motion.div>

            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={springPresets.gentle}
            >
              <ContactCard 
                icon={<Mail className="h-6 w-6" />}
                title="Email"
                content={CONTACT_INFO.email}
                link={`mailto:${CONTACT_INFO.email}`}
              />
              <ContactCard 
                icon={<Phone className="h-6 w-6" />}
                title="Telefone"
                content={CONTACT_INFO.phone}
                link={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`}
              />
              <ContactCard 
                icon={<MapPin className="h-6 w-6" />}
                title="Morada"
                content={CONTACT_INFO.address}
              />
              <ContactCard 
                icon={<Clock className="h-6 w-6" />}
                title="Horário"
                content={CONTACT_INFO.schedule}
              />
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
}